/**
 * signalScoring.js — Pure detection functions for each operational signal.
 *
 * Each detector:
 *  - Receives the full raw timeline (array of runtime events)
 *  - Filters to a 30-minute recency window internally
 *  - Returns a signal object or null
 *  - Is purely synchronous, no side effects
 *
 * Dep rule: imports ONLY from runtimeEvents + signalDefinitions — no selectors.
 */

import { RT_EVENTS }             from "../kevia-runtime/runtimeEvents";
import { SIGNALS, SIGNAL_META }  from "./signalDefinitions";

const WINDOW_MS = 30 * 60 * 1000; // 30-minute session window

function windowedEvents(events) {
  const cutoff = Date.now() - WINDOW_MS;
  return events.filter((e) => e.ts >= cutoff);
}

function mkSignal(id, score, confidence, triggers, relatedEntities = []) {
  return {
    id,
    score,
    confidence,
    triggers,
    expiresAt: Date.now() + WINDOW_MS,
    relatedEntities,
    ...SIGNAL_META[id],
  };
}

function uniqueNames(events) {
  return [...new Set(events.map((e) => e.entityName).filter(Boolean))].slice(0, 3);
}

/* ─── Individual detectors ────────────────────────────────────────────── */

export function detectFollowUpRisk(events) {
  const recent       = windowedEvents(events);
  const entityOpens  = recent.filter(
    (e) => e.type === RT_EVENTS.OPEN_ENTITY &&
           (e.entityType === "family" || e.entityType === "person"),
  );
  const createEvents = recent.filter((e) => e.type === RT_EVENTS.CREATE_RECORD);

  // Multiple entity views without any record creation → follow-up risk
  if (entityOpens.length >= 2 && createEvents.length === 0) {
    const score = entityOpens.length >= 4 ? 0.90 : 0.72;
    const conf  = entityOpens.length >= 4 ? "high" : "medium";
    return mkSignal(SIGNALS.FOLLOW_UP_RISK, score, conf, entityOpens.map((e) => e.id), uniqueNames(entityOpens));
  }
  return null;
}

export function detectInactiveFamilyPattern(events) {
  const recent      = windowedEvents(events);
  const familyEvs   = recent.filter(
    (e) => e.module === "familias" ||
           (e.type === RT_EVENTS.OPEN_ENTITY && e.entityType === "family"),
  );

  if (familyEvs.length >= 3) {
    const score = Math.min(0.92, 0.58 + familyEvs.length * 0.07);
    const conf  = familyEvs.length >= 5 ? "high" : "medium";
    return mkSignal(SIGNALS.INACTIVE_FAMILY_PATTERN, score, conf, familyEvs.map((e) => e.id), uniqueNames(familyEvs));
  }
  return null;
}

export function detectHighVisitActivity(events) {
  const recent    = windowedEvents(events);
  const visitEvs  = recent.filter(
    (e) => e.module === "visitas" ||
           (e.type === RT_EVENTS.CREATE_RECORD && e.entityType === "visit") ||
           (e.type === RT_EVENTS.OPEN_ENTITY   && e.entityType === "visit"),
  );

  if (visitEvs.length >= 3) {
    const score = Math.min(0.88, 0.62 + visitEvs.length * 0.05);
    return mkSignal(SIGNALS.HIGH_VISIT_ACTIVITY, score, "medium", visitEvs.map((e) => e.id));
  }
  return null;
}

export function detectAdminAttention(events) {
  const recent    = windowedEvents(events);
  const adminEvs  = recent.filter(
    (e) => e.module === "operaciones" || e.module === "usuarios" || e.module === "revisión",
  );

  if (adminEvs.length >= 2) {
    return mkSignal(SIGNALS.ADMIN_ATTENTION, 0.68, "medium", adminEvs.map((e) => e.id));
  }
  return null;
}

export function detectAuditInvestigation(events) {
  const recent    = windowedEvents(events);
  const auditEvs  = recent.filter((e) => e.module === "auditoría");

  if (auditEvs.length >= 2) {
    return mkSignal(SIGNALS.AUDIT_INVESTIGATION, 0.82, "high", auditEvs.map((e) => e.id));
  }
  return null;
}

export function detectPrayerFocus(events) {
  const recent     = windowedEvents(events);
  const prayerEvs  = recent.filter(
    (e) => e.module === "oraciones" ||
           (e.type === RT_EVENTS.OPEN_ENTITY && e.entityType === "prayer"),
  );

  if (prayerEvs.length >= 2) {
    const score = Math.min(0.88, 0.62 + prayerEvs.length * 0.07);
    return mkSignal(SIGNALS.PRAYER_FOCUS, score, "medium", prayerEvs.map((e) => e.id));
  }
  return null;
}

export function detectLeadershipReview(events) {
  const recent       = windowedEvents(events);
  const leaderEvs    = recent.filter(
    (e) => e.module === "junta directiva" || e.module === "organigrama",
  );

  if (leaderEvs.length >= 1) {
    return mkSignal(SIGNALS.LEADERSHIP_REVIEW, 0.62, "low", leaderEvs.map((e) => e.id));
  }
  return null;
}

export function detectMemberRecoveryFlow(events) {
  const recent       = windowedEvents(events);
  const personOpens  = recent.filter(
    (e) => e.type === RT_EVENTS.OPEN_ENTITY && e.entityType === "person",
  );
  const followups    = recent.filter(
    (e) => e.type === RT_EVENTS.CREATE_RECORD &&
           (e.entityType === "visit" || e.entityType === "note" || e.entityType === "prayer"),
  );

  if (personOpens.length >= 1 && followups.length >= 1) {
    const names = uniqueNames(personOpens);
    return mkSignal(
      SIGNALS.MEMBER_RECOVERY_FLOW, 0.74, "medium",
      [...personOpens, ...followups].map((e) => e.id),
      names,
    );
  }
  return null;
}
